/// <amd-module name="Likes/LikeHeart" />
import * as React from "react";

import { announce } from "VSS/Core/Util/Accessibility";
import { Button } from "VSSUI/Button";
import { Spinner } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { getId } from "VSS/Core/Util/Object";
import { css } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";
import { format } from "VSS/Core/Util/String";

import { LikesConstants } from "Likes/LikesConstants";
import * as Resources from "Likes/Resources";

export interface LikeCountProps {
    /**
     * number of likes
     */
    likedCount: number;
    /**
     * list of top users who liked, need not be all the liked users
     */
    likedBy?: string[];
    /**
     * callback to fetch list of users who liked
     */
    fetchLikedUsers?: () => void;
}

export interface LikeHeartProps extends LikeCountProps {

    /**
     * Use this class to override the dimensions and styles of the LikeHeart component.
     */
    className?: string;

    /**
     * Callback for when item is lik eheart button is clicked
     */
    onLikeHeartClick: (isLiked: boolean) => void;

    /**
     * Is the item currently liked
     */
    isLiked: boolean;

    /**
     * arialabel to be announced on liked button
     */
    ariaLabelLiked?: string;

    /**
     * arialabel to be announced on unliked button
     */
    ariaLabelUnLiked?: string;
}

const likeCountTooltipId = getId("likes-count");
const likeHeartTooltipId = getId("likes-heart");

/**
 * Component that implements the stateless like heart.
 */
export const LikeHeart: React.StatelessComponent<LikeHeartProps> = (props: LikeHeartProps) => {
    const ariaLabelLiked = props.ariaLabelLiked ? props.ariaLabelLiked : Resources.DefaultLikedAriaLabel;
    const ariaLabelUnLiked = props.ariaLabelUnLiked ? props.ariaLabelUnLiked : Resources.DefaultUnLikedAriaLabel;

    return (
        <div className={css(props.className, "like-heart-component")}>
            <Button
                subtle={true}
                className={"like-heart-button"}
                iconProps={{ iconName: props.isLiked ? "HeartFill" : "Heart" }}
                ariaPressed={props.isLiked}
                onClick={_onIconButtonClick}>
                <span className="like-string">{props.isLiked ? Resources.LikedString : Resources.UnLikedString}</span>
            </Button>
            <LikeCount {...props} />
        </div>
    );

    function _onIconButtonClick(): void {
        props.onLikeHeartClick(!props.isLiked);
        
        if(!props.isLiked) {
            announce(format(Resources.LikesCountAriaLabel, props.likedCount + 1))
        }
    }
}

/**
 * Component that implements the likes count.
 */
export const LikeCount: React.StatelessComponent<LikeCountProps> = (props: LikeCountProps) => {
    const likeCountStringElementWithoutTooltip = <div className="like-count-no-tooltip">
        <span
            className="like-count-element"
            aria-label={format(Resources.LikesCountAriaLabel, props.likedCount)}
        >
            {props.likedCount}
        </span>
    </div>;

    const tooltipContent: string = "";
    const likeCountStringElementWithTooltip = (
        <Tooltip
            anchorOffset={{ horizontal: 0, vertical: -8 }}
            anchorOrigin={{ horizontal: Location.center, vertical: Location.start }}
            id={likeCountTooltipId}
            renderContent={_onRenderTooltipContent}
            showOnFocus={true}
            text={tooltipContent} // content needs to be defined before our custom function gets called
            tooltipOrigin={{ horizontal: Location.center, vertical: Location.end }}
        >           
            <span 
                className="like-count-element focusable"
                aria-label={format(Resources.LikesCountAriaLabel, props.likedCount)}
                tabIndex={0}
                role="note"
            >
                <span aria-hidden={true}>
                    {props.likedCount}
                </span>
            </span>
            
        </Tooltip>
    );

    if (props.likedCount === 0) {
        // do not show tooltip if likedUsers count is 0.
        return likeCountStringElementWithoutTooltip;
    } else if (props.likedBy && props.likedBy.length > 0) {
        return likeCountStringElementWithTooltip;
    } else if (props.fetchLikedUsers) {
        return likeCountStringElementWithTooltip;
    } else {
        return <div />;
    }

    function _getListOfUsersForAriaLabel(): string {
        let likedUsers: string[] = [];
        if (props.likedBy && props.likedBy.length > 0) {
            likedUsers = props.likedBy.map((name, index) => name);
            if ((props.likedBy.length === LikesConstants.TopLikedUsersFetchCount) && props.likedCount > props.likedBy.length) {
                const nmore = (props.likedCount - props.likedBy.length);
                const nMoreUsersResource = (nmore === 1) ? Resources.OneMoreUser : Resources.NMoreUsers;
                likedUsers.push(format(nMoreUsersResource, nmore));
            }
        }
        return likedUsers.join(", ");
    }

    function _onRenderTooltipContent(): JSX.Element {
        if (props.likedBy && props.likedBy.length > 0) {
            const usersLikedList: string[] = props.likedBy;
            const usersLikedListItems: JSX.Element[] = usersLikedList.map((name, index) => <li key={name + index}>{name}</li>);
            if ((props.likedBy.length === LikesConstants.TopLikedUsersFetchCount) && props.likedCount > props.likedBy.length) {
                const nmore = (props.likedCount - props.likedBy.length);
                const nMoreUsersResource = (nmore === 1) ? Resources.OneMoreUser : Resources.NMoreUsers;
                usersLikedListItems.push(<li className="user-item" key={"nmoreusers"}>{format(nMoreUsersResource, nmore)}</li>);
            }
            return (
                <div className="liked-users-tooltip">
                    <ul className="users-list">{usersLikedListItems}</ul>
                </div>
            );
        } else {
            // if liked users info is not available, fetch it.
            if (props.fetchLikedUsers) {
                props.fetchLikedUsers();
            }

            return <Spinner label={Resources.UsersInfoSpinner} />
        }
    }
}
